import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faArrowCircleDown,
} from "@fortawesome/free-solid-svg-icons";
import type { Page, Story } from "../models/story";
import type { StoryReaderLayout } from "../readingLayout";
import { Button } from "../../../components/Button";
import "./StoryControls.css";
import "./StorySpreadReader.css";
import StoryPagePanel from "./StoryPagePanel";
import StorySpreadNavRail from "./StorySpreadNavRail";

type Props = {
  story: Story;
  visiblePages: Page[];
  layout: StoryReaderLayout;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onPrev: () => void;
  onNext: () => void;
  onFinish: () => void;
  onMinimize: () => void;
  onGoToSpread: (spreadIndex: number) => void;
};

/**
 * Read-through UI: one page (mobile) or spread (desktop), nav rail, controls.
 */
const StorySpreadReader = (props: Props) => {
  const {
    story,
    visiblePages,
    layout,
    hasPreviousPage,
    hasNextPage,
    onPrev,
    onNext,
    onFinish,
    onMinimize,
    onGoToSpread,
  } = props;

  const leftPage = visiblePages[0];
  const rightPage = visiblePages[visiblePages.length - 1];

  return (
    <>
      <div className="StoryReaderWithRail StoryReaderScrollRegion">
        <StorySpreadNavRail
          story={story}
          leftPage={leftPage}
          rightPage={rightPage}
          onSelectSpread={onGoToSpread}
        />
        <div
          className={
            "StoryPages StoryPages--withRail" +
            (layout === "single" ? " StoryPages--single" : "")
          }
        >
          {visiblePages.map((page, i) => (
            <StoryPagePanel
              key={`${page.image}-${i}`}
              page={page}
              side={i === 0 ? "left" : "right"}
            />
          ))}
        </div>
      </div>
      <div
        className={
          "StoryControls" +
          (layout === "single" ? " StoryControls--iconNav" : "")
        }
      >
        <Button
          variant="neutral"
          size="sm"
          type="button"
          className="StoryControlNavArrow"
          onClick={onPrev}
          aria-label="Previous page"
          style={{
            visibility: hasPreviousPage ? "visible" : "hidden",
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {layout === "spread" ? (
            <span aria-hidden="true"> Previous</span>
          ) : null}
        </Button>
        <button type="button" className="btn btn-link" onClick={onMinimize}>
          Use Playmat <FontAwesomeIcon icon={faArrowCircleDown} />
        </button>
        {hasNextPage ? (
          <Button
            variant="neutral"
            size="sm"
            type="button"
            className="StoryControlNavArrow"
            onClick={onNext}
            aria-label="Next page"
          >
            {layout === "spread" ? (
              <span aria-hidden="true">Next </span>
            ) : null}
            <FontAwesomeIcon icon={faArrowRight} />
          </Button>
        ) : (
          <Button variant="primary" size="sm" type="button" onClick={onFinish}>
            Finish
          </Button>
        )}
      </div>
    </>
  );
};

export default StorySpreadReader;
